<template>
  <v-main>
    <v-breadcrumbs :items="[ { disabled: true, text: 'Home' } ]" />
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Recently Updated Provider Profiles</h1>
          <p v-if="providers.loading">Loading recent activity...</p>
          <p v-else-if="recent.length">
            Showing all <span v-if="providers.unpublished.length">unpublished</span>
            profiles updated within the last 30 days
          </p>
          <p v-else>
            No recent activity could be found.
          </p>
          <p>
            <v-btn color="primary" :to="{ name: 'provider.index' }">
              View all Provider Profiles
            </v-btn>
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-row v-if="providers.loading">
            <v-col cols="12" sm="6" md="4" lg="3" v-for="n in 16" :key="`loading-sheet-${n}`">
              <v-sheet class="px-3 pt-3 pb-3"
                       color="grey lighten-4">
                <v-skeleton-loader
                  class="mx-auto"
                  max-width="300"
                  type="card" />
              </v-sheet>
            </v-col>
          </v-row>
          <v-row v-else-if="recent.length">
            <v-col cols="12"
                   sm="6"
                   md="4"
                   lg="3"
                   v-for="provider in recent"
                   :key="provider.Id">
              <v-card>
                <v-card-title>
                  <p class="d-inline-block text-truncate">
                    {{ provider.FirstName }} {{ provider.LastName }}
                  </p>
                </v-card-title>
                <v-card-subtitle>
                  {{ provider.Title }}
                </v-card-subtitle>

                <router-link :to="{ name: 'provider.update', params: { id: provider.Id } }">
                  <v-img height="194"
                         :src="getImage(provider.Id)"/>
                </router-link>

                <v-card-text>
                  <p class="my-0">Updated on {{ friendlyDate(provider.UpdatedOn) }}</p>
                  <p :class="{
                        'success--text' : provider.Status === 2,
                        'warning--text' : provider.Status !== 2,
                      }">
                    {{ friendlyStatus(provider.Status) }}
                  </p>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-tooltip top :activator="`#editBtn-${provider.Id}`">
                    {{ $store.getters.can('Editor') || provider.Status === 0
                    ? 'Edit'
                    : 'View'
                    }}
                  </v-tooltip>
                  <v-tooltip top :activator="`#deleteBtn-${provider.Id}`">
                    Delete
                  </v-tooltip>
                  <v-btn icon link
                         color="accent"
                         :id="`editBtn-${provider.Id}`"
                         :to="{ name: 'provider.update', params: { id: provider.Id } }">
                    <v-icon>
                      {{ $store.getters.can('Editor') || provider.Status === 0
                      ? 'edit'
                      : 'visibility'
                      }}
                    </v-icon>
                  </v-btn>
                  <v-btn icon
                         color="accent"
                         v-if="$store.getters.can('Admin')"
                         :id="`deleteBtn-${provider.Id}`"
                         @click="$refs.confirmProvider.open({
                              item: provider,
                              set: providers.items,
                              type: 'Provider',
                             })">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row justify="center" align="center">
        <ConfirmDialog ref="confirmProvider"
                       title="WARNING"
                       width="450"
                       @continue="deleteItem($event)">
          This action will permanently delete this provider
          and remove all references from Locations that may use it.<br>
          <strong>Are you sure you would like to proceed?</strong>
        </ConfirmDialog>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import FriendlyFields from '@/components/Mixins/FriendlyFields';
import { getMediaUrl } from '@/util/Helpers';

export default {
  name: 'Dashboard',
  mixins: [
    FriendlyFields,
  ],
  data() {
    return {
      providers: {
        items: [],
        recent: [],
        unpublished: [],
        loading: true,
      },
    };
  },
  computed: {
    recent() {
      const MAX_COUNT = 50;
      return (this.providers.unpublished.length
        ? this.providers.unpublished
        : this.providers.recent)
        .sort((a, b) => new Date(b.UpdatedOn) - new Date(a.UpdatedOn))
        .slice(0, MAX_COUNT);
    },
  },
  methods: {
    async deleteItem({ item, set, type }) {
      try {
        const i = set.indexOf(item);
        await this.$store.dispatch('delete', `${type}/${item.Id}`);
        set.splice(i, 1);
      } catch (e) {
        // Do nothing
      }
    },
    getImage(id) {
      return getMediaUrl(id, 'Provider', 'Provider.jpg');
    },
  },
  async created() {
    const now = new Date();
    const DAY_RANGE = 30 * 24 * 60 * 60 * 1000;
    const after = new Date(now.getTime() - DAY_RANGE); // Within 30 days
    const providers = await this.$store.dispatch('get', 'Provider');

    this.providers.items = providers.data.Items;
    this.providers.recent = providers.data.Items
      .filter((p) => new Date(p.UpdatedOn) > after);
    this.providers.unpublished = this.providers.recent
      .filter((p) => p.Status < 2);

    this.providers.loading = false;
  },
};
</script>
